import {buildUrl} from '@vio/core/src/utils/url'

const KIWI_BASE = 'https://www.kiwi.com'

/** Query parameter used to attribute Kiwi flight clicks back to a GHA hotel click. */
export const VIO_CLICK_ID_PARAM = 'vio_click_id'

/** Kiwi's affiliate id attributing whitelabel-driven traffic to Vio in their reporting. */
export const KIWI_AFFIL_ID_PARAM = 'affilid'
export const KIWI_AFFIL_ID = 'buy000bookings000vio000vio000partner'

/**
 * Appends Kiwi tracking parameters to a kiwi.com link: `affilid` always, plus
 * `vio_click_id` when a vclid value is present. Returns the href untouched when
 * it is missing (e.g. the active "hotels" item has no href).
 */
const withKiwiTrackingParams = (
  href: string | undefined,
  vioClickId?: string
) =>
  href
    ? buildUrl(href, {
        [KIWI_AFFIL_ID_PARAM]: KIWI_AFFIL_ID,
        ...(vioClickId ? {[VIO_CLICK_ID_PARAM]: vioClickId} : {})
      })
    : href

/**
 * Maps a locale (e.g. "pt-BR") to the Kiwi URL path segment.
 * Kiwi uses country code for Brazilian Portuguese (/br) but
 * language code for most other locales (/pt, /de, /fr, etc.).
 */
const KIWI_LOCALE_MAP: Record<string, string> = {
  'pt-BR': 'br'
}

export const getKiwiLanguageCode = (locale: string): string =>
  KIWI_LOCALE_MAP[locale] ?? locale.split('-')[0]

const KIWI_MAGAZINE_LANGUAGES = new Set(['hu', 'ro', 'cs', 'es', 'pl'])

const getKiwiMagazineUrl = (kiwiLang: string) =>
  KIWI_MAGAZINE_LANGUAGES.has(kiwiLang)
    ? `${KIWI_BASE}/stories/${kiwiLang}/`
    : `${KIWI_BASE}/stories/`

export const getKiwiHomepageUrl = (kiwiLang: string, vioClickId?: string) =>
  withKiwiTrackingParams(`${KIWI_BASE}/${kiwiLang}`, vioClickId) as string

/**
 * `hotelsHref` turns the hotels item into a plain link back to the product,
 * for pages outside it (bookings, account) where it isn't the current section.
 */
export const getNavItems = (
  kiwiLang: string,
  vioClickId?: string,
  hotelsHref?: string
) => [
  {
    id: 'flights' as const,
    href: withKiwiTrackingParams(`${KIWI_BASE}/${kiwiLang}`, vioClickId),
    isActive: false
  },
  {
    id: 'cars' as const,
    href: 'https://www.booking.com/cars/index.html?aid=1549681&label=hotelswl-CARlink-1549681-click',
    isActive: false
  },
  {
    id: 'hotels' as const,
    href: hotelsHref,
    isActive: !hotelsHref
  },
  {
    id: 'magazine' as const,
    href: withKiwiTrackingParams(getKiwiMagazineUrl(kiwiLang), vioClickId),
    isActive: false
  },
  {
    id: 'extras' as const,
    href: withKiwiTrackingParams(`${KIWI_BASE}/${kiwiLang}/extras`, vioClickId),
    isActive: false
  },
  {
    id: 'lastMinute' as const,
    href: withKiwiTrackingParams(
      `${KIWI_BASE}/${kiwiLang}/last-minute-flights/`,
      vioClickId
    ),
    isActive: false
  }
]

export type NavItem = ReturnType<typeof getNavItems>[number]

export const getNavItemLinkProps = (item: NavItem) =>
  item.id === 'hotels'
    ? {href: item.href}
    : {href: item.href, target: '_blank', rel: 'noopener noreferrer'}
